<template>
    <div class="flex flex-col gap-2.5">
        <!-- Search & add (needs the connections-integration flag; existing relationships always render) -->
        <div v-if="canAddRelationships" class="flex flex-col gap-2.5">
            <BaseInput
                v-model="searchInput"
                type="search"
                aria-label="Search for or add team members"
                placeholder="Search for or add team members"
                data-testid="picker-person-search">
                <template #prefix><i class="bi bi-search" aria-hidden="true"></i></template>
            </BaseInput>
            <HorizontalDivider as="hr" spacing="none" />
            <div v-if="searchInput.trim()" class="flex flex-col gap-2.5 max-h-56 overflow-y-auto">
                <div v-if="isSearchPending && !searchResults.length" class="scaffold-body-small text-ink-muted px-1 py-2" data-testid="picker-person-searching">
                    Searching&hellip;
                </div>
                <div v-else-if="!searchResults.length && onlyExistingMatches" class="scaffold-body-small text-ink-muted px-1 py-2" data-testid="picker-person-already-added">
                    They're already on your review list below.
                </div>
                <div v-else-if="!searchResults.length" class="flex flex-col gap-2.5 px-1 py-2" data-testid="picker-person-add-new">
                    <p class="scaffold-body-small text-ink-muted">No team members found — add someone new.</p>
                    <div class="flex flex-col gap-2.5 sm:flex-row sm:items-center">
                        <input v-model="addForm.name" type="text" class="add-person-input" aria-label="Name" placeholder="Name" data-testid="picker-person-new-name" />
                        <input
                            v-model="addForm.jobTitle"
                            type="text"
                            class="add-person-input"
                            aria-label="Title (optional)"
                            placeholder="Title (optional)"
                            data-testid="picker-person-new-title" />
                        <BaseButton
                            theme="cream"
                            size="sm"
                            class="shrink-0"
                            :disabled="isAdding || !addForm.name.trim()"
                            data-testid="picker-person-add-button"
                            @click="addNewPerson">
                            Add
                        </BaseButton>
                    </div>
                </div>
                <button
                    v-for="connection in searchResults"
                    :key="connection.id"
                    type="button"
                    class="group flex items-center gap-2.5 p-1 rounded-md text-start transition-colors hover:bg-surface-hover"
                    :disabled="isAdding"
                    :data-testid="`picker-person-result-${connection.id}`"
                    @click="addPerson(connection)">
                    <PeopleAvatar size="xs" :name="connection.persona.name" :initials="firstInitial(connection.persona.name)" />
                    <span class="flex flex-col min-w-0 grow">
                        <span class="nadia-chat-semibold text-ink truncate">{{ connection.persona.name }}</span>
                        <span v-if="connection.job_title" class="scaffold-details-small text-ink-muted truncate">{{ connection.job_title }}</span>
                    </span>
                    <!-- Clicking the row registers them as a reviewee, which the name alone doesn't say. -->
                    <BaseChip size="2xs" class="shrink-0 min-w-16 justify-center transition-colors group-hover:bg-white" :data-testid="`picker-person-result-add-${connection.id}`">
                        Add
                    </BaseChip>
                </button>
            </div>
            <BaseNotification v-if="addError" theme="error" hide-dismiss :text="addError" />
        </div>
        <HorizontalDivider v-else as="hr" spacing="none" />

        <!-- Existing reviewees -->
        <div v-if="relationshipGroups.length" class="flex flex-col gap-2.5">
            <h3 class="scaffold-body-small text-ink-muted">Reviewing &middot; {{ relationshipGroups.length }} added</h3>
            <div v-for="group in relationshipGroups" :key="group.relationshipId" :data-testid="`picker-person-${group.relationshipId}`">
                <!-- Single task: the row itself starts/resumes. Multiple tasks: the row expands. -->
                <button
                    type="button"
                    class="group flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-start transition-colors hover:bg-surface-hover"
                    :class="{ 'bg-surface-sunken': group.relationshipId === preselectedRelationshipId }"
                    :aria-expanded="group.reviews.length > 1 ? isGroupExpanded(group.relationshipId) : undefined"
                    @click="handleGroupClick(group)">
                    <PeopleAvatar size="xs" :name="group.reviews[0].relationship?.reviewee_name" :initials="firstInitial(group.reviews[0].relationship?.reviewee_name)" />
                    <span class="min-w-0 grow flex flex-col">
                        <span class="nadia-chat-semibold text-ink truncate">{{ group.reviews[0].relationship?.reviewee_name }}</span>
                        <span v-if="group.reviews[0].relationship?.reviewee_job_title" class="scaffold-details-small text-ink-muted truncate">
                            {{ group.reviews[0].relationship?.reviewee_job_title }}
                        </span>
                    </span>
                    <span class="flex items-center gap-3 shrink-0">
                        <StatusChip size="2xs" :state="getReviewGroupStatusState(group.reviews)" class="min-w-24 justify-center">
                            {{ getReviewGroupStatus(group.reviews) }}
                        </StatusChip>
                        <span v-if="group.reviews.length === 1 && isCurrentSession(group.reviews[0])" class="scaffold-details-small text-ink-muted"> Started </span>
                        <i
                            v-else
                            class="bi text-sm/none text-ink-muted transition-colors group-hover:text-ink"
                            :class="group.reviews.length > 1 ? (isGroupExpanded(group.relationshipId) ? 'bi-chevron-up' : 'bi-chevron-down') : 'bi-arrow-right'"
                            aria-hidden="true"></i>
                    </span>
                </button>
                <div v-if="group.reviews.length > 1 && isGroupExpanded(group.relationshipId)" class="pl-10">
                    <PickerRow
                        v-for="review in group.reviews"
                        :key="review.id"
                        :review="review"
                        :current-conversation-hashid="currentConversationHashid"
                        :is-busy="isBusy"
                        :start-disabled="startDisabled"
                        @row-clicked="emit('row-clicked', $event)" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useDebounceFn } from "@vueuse/core";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import PeopleAvatar from "~vue/components/PeopleAvatar.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import { useConnectionsSearch } from "~vue/Moments/composables/useConnectionsSearch";
import { getReviewGroupStatus, getReviewGroupStatusState } from "~vue/Moments/composables/useReviewGroupStatus";
import type { Connection } from "~vue/types/connection";
import type { DecoratedPerformanceReview } from "~vue/types/performance_management";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { sortBy, uniq } from "lodash";
import { computed, reactive, ref, watch } from "vue";

import PickerRow from "../ChatPerformancePickerRow.vue";

interface Props {
    teamReviews: DecoratedPerformanceReview[];
    canAddRelationships: boolean;
    currentConversationHashid?: string;
    isBusy?: boolean;
    startDisabled?: boolean;
    preselectedRelationshipId?: string | null;
    /** Name the user gave Nadia for someone they don't review yet — seeds the search. */
    initialSearch?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
    currentConversationHashid: "",
    isBusy: false,
    startDisabled: false,
    preselectedRelationshipId: null,
    initialSearch: null,
});

const emit = defineEmits<{ "row-clicked": [review: DecoratedPerformanceReview]; "person-added": [] }>();

const firstInitial = (name: string | null | undefined): string => (name ?? "").trim().charAt(0).toUpperCase();

type RelationshipGroup = { relationshipId: string; reviews: DecoratedPerformanceReview[] };

const relationshipGroups = computed<RelationshipGroup[]>(() => {
    const withRelationships = props.teamReviews.filter((review) => !!review.relationship);
    const relationshipIds = uniq(sortBy(withRelationships, (review) => review.relationship?.reviewee_name).map((review) => String(review.relationship?.id)));
    return relationshipIds.map((relationshipId) => ({
        relationshipId,
        reviews: sortBy(
            withRelationships.filter((review) => String(review.relationship?.id) === relationshipId),
            (review) => review.cta?.id,
        ),
    }));
});

const isCurrentSession = (review: DecoratedPerformanceReview) =>
    !!review.conversation?.coaching_session_hashid && review.conversation.coaching_session_hashid === props.currentConversationHashid;

const expandedGroups = ref<Record<string, boolean>>({});
const isGroupExpanded = (relationshipId: string) => expandedGroups.value[relationshipId] ?? relationshipId === props.preselectedRelationshipId;

const handleGroupClick = (group: RelationshipGroup) => {
    if (group.reviews.length === 1) {
        if (!isCurrentSession(group.reviews[0])) {
            emit("row-clicked", group.reviews[0]);
        }
        return;
    }
    expandedGroups.value[group.relationshipId] = !isGroupExpanded(group.relationshipId);
    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_PICKER_GROUP_CLICKED, {
        relationship_id: group.relationshipId,
        is_expanded: expandedGroups.value[group.relationshipId],
        review_count: group.reviews.length,
    });
};

// --- Search & add ---

const searchInput = ref(props.canAddRelationships ? (props.initialSearch?.trim() ?? "") : "");
const { connections, searchQuery, isFetchingConnections, connectionsFetchError } = useConnectionsSearch();
searchQuery.value = searchInput.value;
const syncSearchQuery = useDebounceFn(() => {
    searchQuery.value = searchInput.value;
}, 500);
watch(searchInput, () => {
    syncSearchQuery();
});

const existingRevieweeIds = computed(() => new Set(props.teamReviews.map((review) => review.relationship?.reviewee_id).filter(Boolean)));
const searchResults = computed<Connection[]>(() => (connections.value ?? []).filter((connection) => !existingRevieweeIds.value.has(connection.id)));

// The search matched, but everyone it matched is already being reviewed (filtered out of
// searchResults) — point at the list below instead of offering to add a duplicate.
const onlyExistingMatches = computed(() => (connections.value ?? []).length > 0);

// Never offer the create-new form over a search that hasn't landed: `initialSearch` renders this
// panel before its first request resolves, so a fast click would create a second connection for
// someone the search was about to return. A failed search still opens it — an unknown answer
// shouldn't make adding anyone impossible.
const isSearchPending = computed(() => isFetchingConnections.value || (connections.value === null && !connectionsFetchError.value));

const addError = ref("");
const isAdding = ref(false);
const addPayload = ref<{ reviewee_id: string } | null>(null);
const { execute: executeAdd } = useFetch("/api/coach/performance_reviews/relationships/", { immediate: false }).post(addPayload).json();

// Register an existing connection as a reviewee in the current performance moment.
const registerReviewee = async (connectionId: string) => {
    addPayload.value = { reviewee_id: connectionId };
    await executeAdd(true);
};

// Both add paths end here, and both create a review relationship in the current moment —
// `is_new_connection` distinguishes registering someone who already existed from adding
// someone brand new.
const finishAdd = (connectionId: string, isNewConnection: boolean) => {
    searchInput.value = "";
    searchQuery.value = "";
    addForm.name = "";
    addForm.jobTitle = "";
    autoFilledName.value = "";
    logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_RELATIONSHIP_CREATED, {
        connection_id: connectionId,
        is_new_connection: isNewConnection,
        source: "chat_picker",
    });
    emit("person-added");
};

const addPerson = async (connection: Connection) => {
    if (isAdding.value) {
        return;
    }
    isAdding.value = true;
    addError.value = "";
    try {
        await registerReviewee(connection.id);
        finishAdd(connection.id, false);
    } catch {
        addError.value = "Couldn't add that person — please try again.";
    } finally {
        isAdding.value = false;
    }
};

// --- Add someone new (no matching connection) ---

const addForm = reactive({ name: searchInput.value, jobTitle: "" });

// Mirror the search text into the name field while it still holds our last auto-fill,
// but stop the moment the user edits the name themselves (same pattern as AddPeopleDialog).
const autoFilledName = ref(searchInput.value);
watch(searchInput, () => {
    const query = searchInput.value.trim();
    if (!addForm.name || addForm.name === autoFilledName.value) {
        addForm.name = query;
        autoFilledName.value = query;
    }
});

const createPayload = ref<{ job_title: string; persona: { name: string } } | null>(null);
const { data: createdConnection, error: createConnectionError, execute: executeCreate } = useFetch("/api/connections/", { immediate: false }).post(createPayload).json();

const addNewPerson = async () => {
    if (isAdding.value || !addForm.name.trim()) {
        return;
    }
    isAdding.value = true;
    addError.value = "";
    try {
        createPayload.value = { job_title: addForm.jobTitle.trim(), persona: { name: addForm.name.trim() } };
        await executeCreate(true);
        const connectionId: string = createdConnection.value.id;
        logUserInteraction(AmplitudeEventNames.CONNECTION_ADDED, { source: "performance_review" });
        await registerReviewee(connectionId);
        finishAdd(connectionId, true);
    } catch {
        if (!isSessionEndedError(createConnectionError.value)) {
            addError.value = "Couldn't add that person — please try again.";
        }
    } finally {
        isAdding.value = false;
    }
};
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .add-person-input {
        @apply w-full min-w-0 grow rounded-md border border-control-border bg-control-bg p-2.5 outline-none transition-colors scaffold-body-small text-control-text-strong placeholder:text-control-placeholder focus:border-control-border-focus;
    }
}
</style>
